import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { adminApi, type AdminShareBookDetail } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  X,
  Save,
  Building2,
  BookOpen,
  Lock,
  Unlock,
  Users,
  ArrowRightLeft,
  Layers,
  CheckCircle,
  XCircle
} from "lucide-react";
import { formatCurrency } from "@/lib/format";

interface AdminShareBookDetailModalProps {
  shareBookId: string;
  onClose: () => void;
  onUpdated: () => void;
}

const transactionTypeLabels: Record<string, string> = {
  founding: "Bolagsbildning",
  issuance: "Nyemission",
  transfer: "Överlåtelse",
  split: "Split",
  reverse_split: "Omvänd split",
  redemption: "Inlösen",
  bonus_issue: "Fondemission",
  conversion: "Konvertering",
};

export function AdminShareBookDetailModal({
  shareBookId,
  onClose,
  onUpdated
}: AdminShareBookDetailModalProps) {
  const [shareBookDetail, setShareBookDetail] = useState<AdminShareBookDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  // Edit form state
  const [isLocked, setIsLocked] = useState(false);
  const [lockedReason, setLockedReason] = useState("");

  useEffect(() => {
    loadShareBook();
  }, [shareBookId]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const loadShareBook = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminApi.getShareBook(shareBookId);
      if (response.success && response.data) {
        const sb = response.data;
        setShareBookDetail(sb);
        setIsLocked(sb.isLocked);
        setLockedReason(sb.lockedReason || "");
      } else {
        setError(response.error?.message || "Kunde inte ladda aktieboken");
      }
    } catch {
      setError("Kunde inte ansluta till servern");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const response = await adminApi.updateShareBook(shareBookId, {
        isLocked,
        lockedReason: isLocked ? (lockedReason || "Låst av administratör") : null,
      });
      if (response.success) {
        onUpdated();
        toast.success("Aktieboken har uppdaterats");
        onClose();
      } else {
        setError(response.error?.message || "Kunde inte uppdatera aktieboken");
      }
    } catch {
      toast.error("Kunde inte ansluta till servern");
    } finally {
      setIsSaving(false);
    }
  };

  // Track mousedown to prevent closing when dragging from inside modal
  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownTargetRef.current = e.target;
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (
      e.target === overlayRef.current &&
      mouseDownTargetRef.current === overlayRef.current
    ) {
      onClose();
    }
    mouseDownTargetRef.current = null;
  };

  const renderClauseStatus = (hasClause: boolean, label: string) => (
    <div className="flex items-center gap-2">
      {hasClause ? (
        <CheckCircle size={14} className="text-green-500" />
      ) : (
        <XCircle size={14} className="text-gray-300" />
      )}
      <span className={hasClause ? "text-gray-700" : "text-gray-400"}>{label}</span>
    </div>
  );

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/20"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <BookOpen size={20} className="text-primary-600" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Aktiebok - Detaljer</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : error && !shareBookDetail ? (
            <div className="text-center py-8">
              <p className="text-red-600">{error}</p>
            </div>
          ) : shareBookDetail ? (
            <div className="space-y-5">
              {/* Company Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg border border-gray-200">
                    <Building2 size={20} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{shareBookDetail.companyName}</p>
                    <p className="text-sm text-gray-500">{shareBookDetail.organizationNumber}</p>
                  </div>
                </div>
              </div>

              {/* Statistics Grid */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-blue-700">{formatCurrency(shareBookDetail.shareCapital)}</p>
                  <p className="text-xs text-blue-600">Aktiekapital</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-700">{(shareBookDetail.totalShares ?? 0).toLocaleString("sv-SE")}</p>
                  <p className="text-xs text-green-600">Totalt antal aktier</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-purple-700">{shareBookDetail.activeShareholders}</p>
                  <p className="text-xs text-purple-600">Aktiva aktieägare</p>
                </div>
              </div>

              {/* Share Classes */}
              {shareBookDetail.shareClasses.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
                    <Layers size={14} />
                    Aktieslag ({shareBookDetail.shareClasses.length})
                  </h3>
                  <div className="space-y-2">
                    {shareBookDetail.shareClasses.map((sc) => (
                      <div key={sc.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-800">{sc.name}</p>
                          <p className="text-xs text-gray-500">
                            {(sc.totalShares ?? 0).toLocaleString("sv-SE")} aktier
                            {sc.votingPower && sc.votingPower !== 1 && ` (${sc.votingPower} röst${sc.votingPower !== 1 ? 'er' : ''}/aktie)`}
                          </p>
                        </div>
                        <p className="text-sm font-medium text-gray-600">
                          {formatCurrency(sc.parValue)}/aktie
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Shareholders */}
              {shareBookDetail.shareholders.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
                    <Users size={14} />
                    Aktieägare ({shareBookDetail.totalShareholders})
                  </h3>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {shareBookDetail.shareholders.map((sh) => (
                      <div key={sh.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-800">{sh.name}</p>
                          <p className="text-xs text-gray-500">
                            {sh.type === "person" ? "Fysisk person" : sh.type === "company" ? "Juridisk person" : sh.type === "estate" ? "Dödsbo" : "Stiftelse/Trust"}
                            {(sh.personalId || sh.orgNumber) && ` - ${sh.personalId || sh.orgNumber}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${sh.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                            {sh.isActive ? "Aktiv" : "Inaktiv"}
                          </span>
                        </div>
                      </div>
                    ))}
                    {shareBookDetail.totalShareholders > shareBookDetail.shareholders.length && (
                      <p className="text-xs text-gray-400 text-center py-2">
                        +{shareBookDetail.totalShareholders - shareBookDetail.shareholders.length} fler aktieägare
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Recent Transactions */}
              {shareBookDetail.recentTransactions.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2 mb-3">
                    <ArrowRightLeft size={14} />
                    Senaste transaktioner
                  </h3>
                  <div className="space-y-2 max-h-[150px] overflow-y-auto">
                    {shareBookDetail.recentTransactions.map((tx) => (
                      <div key={tx.id} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                              {transactionTypeLabels[tx.type] || tx.type}
                            </span>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(tx.transactionDate).toLocaleDateString("sv-SE")}
                            </p>
                          </div>
                          <p className="text-sm font-medium text-gray-700">
                            {(tx.quantity ?? 0).toLocaleString("sv-SE")} aktier
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Clauses */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Förbehåll i bolagsordningen</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {renderClauseStatus(shareBookDetail.hasPreemptionClause, "Förköpsförbehåll")}
                  {renderClauseStatus(shareBookDetail.hasConsentClause, "Samtyckesförbehåll")}
                  {renderClauseStatus(shareBookDetail.hasRedemptionClause, "Hembudsförbehåll")}
                  {renderClauseStatus(shareBookDetail.hasConversionClause, "Omvandlingsförbehåll")}
                </div>
              </div>

              {/* Lock Status Control */}
              <div className="border-t pt-5">
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  {isLocked ? <Lock size={14} /> : <Unlock size={14} />}
                  Låsstatus
                </h3>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isLocked}
                      onChange={(e) => {
                        setIsLocked(e.target.checked);
                        if (!e.target.checked) setLockedReason("");
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Lås aktieboken</span>
                  </label>

                  {isLocked && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Anledning (valfri)</label>
                      <input
                        type="text"
                        value={lockedReason}
                        onChange={(e) => setLockedReason(e.target.value)}
                        placeholder="Ange anledning för låsning..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  )}

                  <p className="text-xs text-gray-500">
                    {isLocked
                      ? "En låst aktiebok kan inte redigeras av användaren. Du kan låsa upp den när som helst."
                      : "Aktieboken är olåst och kan redigeras av behöriga användare."
                    }
                  </p>
                </div>
              </div>

              {/* Info box */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-xs text-gray-500">
                  <span className="font-medium">ID:</span> {shareBookDetail.id}
                </p>
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Skapad:</span>{" "}
                  {new Date(shareBookDetail.createdAt).toLocaleString("sv-SE")}
                </p>
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Uppdaterad:</span>{" "}
                  {new Date(shareBookDetail.updatedAt).toLocaleString("sv-SE")}
                </p>
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {shareBookDetail && (
          <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-200">
            <Button variant="outline" onClick={onClose}>
              Avbryt
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Spinner size="sm" /> : <Save size={16} />}
              Spara ändringar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
